import React, { useState, useEffect } from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { 
  Plus, 
  Home,
  Calendar,
  Trash2,
  Edit3,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Check,
  DollarSign,
  TrendingUp,
  Briefcase,
  Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useIsMobile } from '../../hooks/useMediaQuery';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { toast } from 'sonner';

// Milestone labels
const MILESTONE_LABELS = [
  'Deposited Escrow Money',
  'Sent Compliance Documents',
  'Home Inspection',
  'Pest Inspection',
  'Request Repairs',
  'Appraisal',
  'Final Walkthrough'
];

// Lead source options
const LEAD_SOURCE_OPTIONS = [
  'Referral',
  'Open House',
  'Online Lead',
  'Sphere of Influence',
  'Sign Call',
  'Past Client',
  'Expired Listing',
  'FSBO',
  'Builder',
  'Other'
];

// Commission percent options
const COMMISSION_PERCENT_OPTIONS = ['2', '2.5', '3', '3.5', '4', '5', '6'];

const MyDealsPanel = () => {
  const { getToken } = useClerkAuth();
  const backendUrl = API_BASE_URL;
  const isMobile = useIsMobile();
  
  // State
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'
  const [editingDeal, setEditingDeal] = useState(null);
  const [expandedMilestones, setExpandedMilestones] = useState({});
  
  // Pagination state for deal lists
  const [activeDealsVisible, setActiveDealsVisible] = useState(5);
  const [closedDealsVisible, setClosedDealsVisible] = useState(5);
  
  // Form state
  const today = new Date().toISOString().slice(0, 10);
  const [formData, setFormData] = useState({
    house_address: '',
    amount_sold_for: '',
    commission_percent: '3',
    split_percent: '100',
    team_brokerage_split_percent: '0',
    lead_source: '',
    contract_signed: today,
    closing_date: today,
    due_diligence_start: '',
    due_diligence_over: ''
  });
  const [saving, setSaving] = useState(false);

  // Fetch all deals on mount
  useEffect(() => {
    fetchAllDeals();
  }, []);

  const fetchAllDeals = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await axios.get(`${backendUrl}/api/pnl/all-deals`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast.error('Failed to load deals');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  // Split deals into active and closed
  const todayStr = new Date().toISOString().slice(0, 10);
  const activeDeals = deals.filter(d => d.closing_date >= todayStr);
  const closedDeals = deals.filter(d => d.closing_date < todayStr);
  
  // Stats calculations
  const currentYear = new Date().getFullYear();
  const closedThisYear = closedDeals.filter(d => 
    new Date(d.closing_date).getFullYear() === currentYear
  ).length;
  const totalVolume = deals.reduce((sum, d) => sum + (d.amount_sold_for || 0), 0);
  const totalCommission = deals.reduce((sum, d) => sum + (d.final_income || 0), 0);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date as MM/DD/YY
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  // Calculate DD status
  const calculateDDStatus = (ddStart, ddOver) => {
    if (!ddOver || ddOver === "") return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overDate = new Date(ddOver);
    overDate.setHours(0, 0, 0, 0);
    
    const diffTime = overDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'DD Complete', color: 'bg-gray-100 text-gray-600' };
    } else if (diffDays <= 7) {
      return { text: `${diffDays} days left`, color: 'bg-orange-100 text-orange-700' };
    } else {
      return { text: `${diffDays} days left`, color: 'bg-green-100 text-green-700' };
    }
  };

  // Handle milestone toggle
  const handleMilestoneToggle = async (dealId, index) => {
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;
    
    const newMilestones = [...(deal.milestones || [false, false, false, false, false, false, false])];
    newMilestones[index] = !newMilestones[index];
    
    // Optimistic update
    setDeals(deals.map(d => 
      d.id === dealId ? { ...d, milestones: newMilestones } : d
    ));
    
    try {
      const token = await getToken();
      await axios.patch(
        `${backendUrl}/api/pnl/deals/${dealId}/milestones`,
        newMilestones,
        {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      toast.success('Milestone saved');
    } catch (error) {
      console.error('Error saving milestone:', error);
      toast.error('Failed to save milestone');
      // Revert on error
      fetchAllDeals();
    }
  };

  // Toggle milestone expansion
  const toggleMilestoneExpansion = (dealId) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [dealId]: !prev[dealId]
    }));
  };

  // Handle form input change
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      house_address: '',
      amount_sold_for: '',
      commission_percent: '3',
      split_percent: '100',
      team_brokerage_split_percent: '0',
      lead_source: '',
      contract_signed: today,
      closing_date: today,
      due_diligence_start: '',
      due_diligence_over: ''
    });
  };

  // Handle add deal
  const handleAddDeal = () => {
    resetForm();
    setView('add');
  };

  // Handle edit deal
  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setFormData({
      house_address: deal.house_address || '',
      amount_sold_for: deal.amount_sold_for?.toString() || '',
      commission_percent: deal.commission_percent?.toString() || '3',
      split_percent: deal.split_percent?.toString() || '100',
      team_brokerage_split_percent: deal.team_brokerage_split_percent?.toString() || '0',
      lead_source: deal.lead_source || '',
      contract_signed: deal.contract_signed || '',
      closing_date: deal.closing_date || '',
      due_diligence_start: deal.due_diligence_start || '',
      due_diligence_over: deal.due_diligence_over || ''
    });
    setView('edit');
  };

  // Handle save deal (create or update)
  const handleSaveDeal = async () => {
    // Validation
    if (!formData.house_address || !formData.amount_sold_for || !formData.lead_source || !formData.closing_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      const dealPayload = {
        house_address: formData.house_address,
        amount_sold_for: parseFloat(formData.amount_sold_for),
        commission_percent: parseFloat(formData.commission_percent),
        split_percent: parseFloat(formData.split_percent),
        team_brokerage_split_percent: parseFloat(formData.team_brokerage_split_percent),
        lead_source: formData.lead_source,
        contract_signed: formData.contract_signed || '',
        closing_date: formData.closing_date,
        due_diligence_start: formData.due_diligence_start || '',
        due_diligence_over: formData.due_diligence_over || ''
      };

      if (view === 'add') {
        await axios.post(`${backendUrl}/api/pnl/deals`, dealPayload, {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        toast.success('Deal created successfully');
      } else {
        await axios.patch(`${backendUrl}/api/pnl/deals/${editingDeal.id}`, dealPayload, {
          withCredentials: true,
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        toast.success('Deal updated successfully');
      }

      await fetchAllDeals();
      setView('list');
      resetForm();
      setEditingDeal(null);
    } catch (error) {
      console.error('Error saving deal:', error);
      toast.error('Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete deal
  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;

    try {
      const token = await getToken();
      await axios.delete(`${backendUrl}/api/pnl/deals/${dealId}`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      toast.success('Deal deleted');
      await fetchAllDeals();
    } catch (error) {
      console.error('Error deleting deal:', error);
      toast.error('Failed to delete deal');
    }
  };

  // Render milestone progress bar
  const renderMilestoneProgress = (deal) => {
    const milestones = deal.milestones || [false, false, false, false, false, false, false];
    const completed = milestones.filter(Boolean).length;
    const isExpanded = expandedMilestones[deal.id];

    return (
      <div className="mt-4 border-t pt-4">
        {/* Progress bar */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">{completed} of 7 milestones</span>
          <button
            onClick={() => toggleMilestoneExpansion(deal.id)}
            className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${
              isMobile 
                ? 'bg-primary text-white' 
                : 'text-primary hover:underline'
            }`}
            data-testid={`toggle-milestones-${deal.id}`}
          >
            {isExpanded ? 'Hide Milestones' : 'View Milestones'}
            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>
        
        {/* Progress track */}
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(completed / 7) * 100}%` }}
          />
        </div>

        {/* Expanded milestone stepper - Mobile friendly with larger checkboxes */}
        {isExpanded && (
          <div className="mt-4 space-y-2">
            <p className="text-xs text-gray-400 mb-2">
              {isMobile ? 'Tap to mark milestones complete' : 'Click a circle to mark a milestone complete'}
            </p>
            {isMobile ? (
              // Mobile: Vertical checklist with large 28px checkboxes
              <div className="space-y-3">
                {milestones.map((checked, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleMilestoneToggle(deal.id, idx)}
                    className="flex items-center gap-3 w-full text-left"
                    data-testid={`milestone-${deal.id}-${idx}`}
                  >
                    <div className={`w-7 h-7 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                      checked 
                        ? 'bg-primary border-primary text-white' 
                        : 'bg-white border-gray-300'
                    }`}>
                      {checked && <Check className="w-4 h-4" />}
                    </div>
                    <span className={`text-sm ${checked ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                      {MILESTONE_LABELS[idx]}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              // Desktop: Horizontal stepper with circles
              <>
                <div className="flex justify-between items-center">
                  {milestones.map((checked, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleMilestoneToggle(deal.id, idx)}
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                        checked 
                          ? 'bg-primary border-primary text-white' 
                          : 'bg-white border-gray-300 hover:border-primary'
                      }`}
                      title={MILESTONE_LABELS[idx]}
                      data-testid={`milestone-${deal.id}-${idx}`}
                    >
                      {checked && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  {MILESTONE_LABELS.map((label, idx) => (
                    <span key={idx} className="flex-1 text-center px-1 leading-tight" title={label}>
                      {label}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render active deal card
  const renderActiveDealCard = (deal) => {
    const ddStatus = calculateDDStatus(deal.due_diligence_start, deal.due_diligence_over);

    return (
      <Card key={deal.id} className="mb-4" data-testid={`deal-card-${deal.id}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Home className="w-4 h-4 text-gray-400" />
                <h3 className="font-semibold text-gray-900">{deal.house_address}</h3>
              </div>
              <div className="flex flex-wrap gap-2 text-sm text-gray-500 mb-2">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {deal.lead_source}
                </span>
                {deal.contract_signed && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Contract: {formatDate(deal.contract_signed)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {ddStatus && (
                  <Badge className={ddStatus.color}>{ddStatus.text}</Badge>
                )}
                <span className="flex items-center gap-1 text-sm">
                  <Clock className="w-3 h-3 text-gray-400" />
                  Closing: {formatDate(deal.closing_date)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-green-600">
                {formatCurrency(deal.final_income || 0)}
              </div>
              <div className="flex gap-1 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditDeal(deal)}
                  data-testid={`edit-deal-${deal.id}`}
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteDeal(deal.id)}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`delete-deal-${deal.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Milestone progress */}
          {renderMilestoneProgress(deal)}
        </CardContent>
      </Card>
    );
  };

  // Render form (add/edit)
  const renderForm = () => (
    <div className="p-6">
      <button
        onClick={() => { setView('list'); setEditingDeal(null); resetForm(); }}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        data-testid="back-to-deals"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to My Deals
      </button>

      <h1 className="text-2xl font-bold mb-6">
        {view === 'add' ? 'Add New Deal' : 'Edit Deal'}
      </h1>

      <div className="max-w-2xl space-y-6">
        {/* Property Address */}
        <div>
          <Label htmlFor="house_address">Property Address *</Label>
          <Input
            id="house_address"
            value={formData.house_address}
            onChange={(e) => handleInputChange('house_address', e.target.value)}
            placeholder="123 Main St, City, State"
            data-testid="input-house-address"
          />
        </div>

        {/* Amount & Commission Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="amount_sold_for">Amount Sold For *</Label>
            <Input
              id="amount_sold_for"
              type="number"
              value={formData.amount_sold_for}
              onChange={(e) => handleInputChange('amount_sold_for', e.target.value)}
              placeholder="500000"
              data-testid="input-amount"
            />
          </div>
          <div>
            <Label htmlFor="commission_percent">Commission % *</Label>
            <Select
              value={formData.commission_percent}
              onValueChange={(value) => handleInputChange('commission_percent', value)}
            >
              <SelectTrigger data-testid="select-commission">
                <SelectValue placeholder="Select commission" />
              </SelectTrigger>
              <SelectContent>
                {COMMISSION_PERCENT_OPTIONS.map(opt => (
                  <SelectItem key={opt} value={opt}>{opt}%</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Split Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="split_percent">Agent's Share %</Label>
            <Input
              id="split_percent"
              type="number"
              value={formData.split_percent}
              onChange={(e) => handleInputChange('split_percent', e.target.value)}
              placeholder="100"
              data-testid="input-split"
            />
          </div>
          <div>
            <Label htmlFor="team_brokerage_split_percent">Team/Brokerage Split %</Label>
            <Input
              id="team_brokerage_split_percent"
              type="number"
              value={formData.team_brokerage_split_percent}
              onChange={(e) => handleInputChange('team_brokerage_split_percent', e.target.value)}
              placeholder="0"
              data-testid="input-team-split"
            />
          </div>
        </div>

        {/* Lead Source */}
        <div>
          <Label htmlFor="lead_source">Lead Source *</Label>
          <Select
            value={formData.lead_source}
            onValueChange={(value) => handleInputChange('lead_source', value)}
          >
            <SelectTrigger data-testid="select-lead-source">
              <SelectValue placeholder="Select lead source" />
            </SelectTrigger>
            <SelectContent>
              {LEAD_SOURCE_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Row 1 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="contract_signed">Contract Signed</Label>
            <Input
              id="contract_signed"
              type="date"
              value={formData.contract_signed}
              onChange={(e) => handleInputChange('contract_signed', e.target.value)}
              data-testid="input-contract-signed"
            />
          </div>
          <div>
            <Label htmlFor="closing_date">Closing Date *</Label>
            <Input
              id="closing_date"
              type="date"
              value={formData.closing_date}
              onChange={(e) => handleInputChange('closing_date', e.target.value)}
              data-testid="input-closing-date"
            />
          </div>
        </div>

        {/* Date Row 2 - DD */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="due_diligence_start">Due Diligence Start</Label>
            <Input
              id="due_diligence_start"
              type="date"
              value={formData.due_diligence_start}
              onChange={(e) => handleInputChange('due_diligence_start', e.target.value)}
              data-testid="input-dd-start"
            />
          </div>
          <div>
            <Label htmlFor="due_diligence_over">Due Diligence Over</Label>
            <Input
              id="due_diligence_over"
              type="date"
              value={formData.due_diligence_over}
              onChange={(e) => handleInputChange('due_diligence_over', e.target.value)}
              data-testid="input-dd-over"
            />
          </div>
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSaveDeal}
          disabled={saving}
          className="w-full bg-primary hover:bg-primary/90"
          data-testid="save-deal-btn"
        >
          {saving ? 'Saving...' : (view === 'add' ? 'Add Deal' : 'Save Changes')}
        </Button>
      </div>
    </div>
  );

  // Render list view
  const renderListView = () => (
    <div className={isMobile ? "p-4" : "p-6"}>
      <div className="flex justify-between items-center mb-6">
        <h1 className={isMobile ? "text-xl font-bold" : "text-2xl font-bold"}>My Deals</h1>
        <Button onClick={handleAddDeal} className={isMobile ? "w-auto" : ""} data-testid="add-deal-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Deal
        </Button>
      </div>

      {/* Summary Stats - 2x2 on mobile, 4 cols on desktop */}
      <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-4 mb-8`}>
        <Card>
          <CardContent className={`${isMobile ? 'p-3' : 'p-4'} text-center`}>
            <div className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-primary`}>{activeDeals.length}</div>
            <div className="text-xs text-gray-500">Active Deals</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className={`${isMobile ? 'p-3' : 'p-4'} text-center`}>
            <div className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-blue-600`}>{closedThisYear}</div>
            <div className="text-xs text-gray-500">Closed This Year</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className={`${isMobile ? 'p-3' : 'p-4'} text-center`}>
            <div className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-gray-700`}>{formatCurrency(totalVolume)}</div>
            <div className="text-xs text-gray-500">Total Volume</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className={`${isMobile ? 'p-3' : 'p-4'} text-center`}>
            <div className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-green-600`}>{formatCurrency(totalCommission)}</div>
            <div className="text-xs text-gray-500">Total Commission</div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Active Deals Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold">Active Deals ({activeDeals.length})</h2>
            </div>
            {activeDeals.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  No active deals. Click "Add Deal" to create one.
                </CardContent>
              </Card>
            ) : (
              <>
                {activeDeals.slice(0, activeDealsVisible).map(deal => renderActiveDealCard(deal))}
                {activeDealsVisible < activeDeals.length && (
                  <button
                    onClick={() => setActiveDealsVisible(prev => prev + 5)}
                    className="w-full py-3 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                    data-testid="active-deals-show-more"
                  >
                    Show More ({activeDeals.length - activeDealsVisible} remaining)
                  </button>
                )}
                {activeDealsVisible >= activeDeals.length && activeDeals.length > 5 && (
                  <button
                    onClick={() => setActiveDealsVisible(5)}
                    className="w-full py-3 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    data-testid="active-deals-show-less"
                  >
                    Show Less
                  </button>
                )}
              </>
            )}
          </div>

          {/* Closed Deals Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold">Closed Deals ({closedDeals.length})</h2>
            </div>
            {closedDeals.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  No closed deals yet.
                </CardContent>
              </Card>
            ) : isMobile ? (
              // Mobile: Card-based layout for closed deals
              <>
                <div className="space-y-4">
                  {closedDeals.slice(0, closedDealsVisible).map(deal => (
                    <Card key={deal.id} data-testid={`closed-deal-card-${deal.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{deal.house_address}</h3>
                            <div className="text-sm text-gray-500 space-y-1">
                              <div>{deal.lead_source}</div>
                              <div>Closed: {formatDate(deal.closing_date)}</div>
                              <div>Sale: {formatCurrency(deal.amount_sold_for)}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-green-600 mb-2">
                              {formatCurrency(deal.final_income)}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditDeal(deal)}
                                data-testid={`edit-closed-deal-${deal.id}`}
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDeal(deal.id)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-closed-deal-${deal.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {closedDealsVisible < closedDeals.length && (
                  <button
                    onClick={() => setClosedDealsVisible(prev => prev + 5)}
                    className="w-full py-3 mt-4 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                    data-testid="closed-deals-show-more"
                  >
                    Show More ({closedDeals.length - closedDealsVisible} remaining)
                  </button>
                )}
                {closedDealsVisible >= closedDeals.length && closedDeals.length > 5 && (
                  <button
                    onClick={() => setClosedDealsVisible(5)}
                    className="w-full py-3 mt-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    data-testid="closed-deals-show-less"
                  >
                    Show Less
                  </button>
                )}
              </>
            ) : (
              // Desktop: Table layout for closed deals
              <>
                <Card>
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-4 font-medium text-gray-600">Property Address</th>
                          <th className="text-left p-4 font-medium text-gray-600">Lead Source</th>
                          <th className="text-left p-4 font-medium text-gray-600">Closing Date</th>
                          <th className="text-right p-4 font-medium text-gray-600">Sale Price</th>
                          <th className="text-right p-4 font-medium text-gray-600">Final Income</th>
                          <th className="text-center p-4 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {closedDeals.slice(0, closedDealsVisible).map(deal => (
                          <tr key={deal.id} className="border-b hover:bg-gray-50" data-testid={`closed-deal-row-${deal.id}`}>
                            <td className="p-4">{deal.house_address}</td>
                            <td className="p-4">{deal.lead_source}</td>
                            <td className="p-4">{formatDate(deal.closing_date)}</td>
                            <td className="p-4 text-right">{formatCurrency(deal.amount_sold_for)}</td>
                            <td className="p-4 text-right text-green-600 font-medium">{formatCurrency(deal.final_income)}</td>
                            <td className="p-4 text-center">
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditDeal(deal)}
                                  data-testid={`edit-closed-deal-${deal.id}`}
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteDeal(deal.id)}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`delete-closed-deal-${deal.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
                {closedDealsVisible < closedDeals.length && (
                  <button
                    onClick={() => setClosedDealsVisible(prev => prev + 5)}
                    className="w-full py-3 mt-4 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                    data-testid="closed-deals-show-more"
                  >
                    Show More ({closedDeals.length - closedDealsVisible} remaining)
                  </button>
                )}
                {closedDealsVisible >= closedDeals.length && closedDeals.length > 5 && (
                  <button
                    onClick={() => setClosedDealsVisible(5)}
                    className="w-full py-3 mt-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    data-testid="closed-deals-show-less"
                  >
                    Show Less
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Main render
  return view === 'list' ? renderListView() : renderForm();
};

export default MyDealsPanel;
